<template>
  <main class="choose-tickets">
    <h1 class="title title--desktop">{{ headingText }}</h1>
    <div class="pp-preamble">
      <h1 class="title title--mobile">{{ headingText }}</h1>
    </div>
    <div class="pp-layout-container pp-layout-container--single">
      <error v-if="displayError">{{ errorMessage }}</error>
      <section class="pp-main-content">
        <table class="pp-table pp-table--choose-tickets">
          <!--
            * This is the header row for ticket items.
            * The number of columns matches those in ticket-item
          -->
          <tr class="tt tt--header">
            <th class="tt__col tt__col-1">{{ $t("ticket.type") }}</th>
            <th class="tt__col tt__col-2">{{ $t("ticket.price") }}</th>
            <th class="tt__col tt__col-3">{{ $t("ticket.quantity") }}</th>
          </tr>
          <!--
            * Iterating over ticket items
            * @updateTicketsSelected listens for changes to the number of tickets selected
          -->
          <ticket-item
            v-for="ticket in performance.ticketTypes"
            :key="ticket.ticket_type"
            :ticketInfo="ticket"
            :ticketsRequired="ticketsRequired"
            :ticketCount="ticketCount"
            :ticketTypeLastModified="ticketTypeLastModified"
            :priceClassCode="ticket.priceClassCode"
            :passwordRequired="ticket.passwordRequired"
            @updateTicketsSelected="updateTicketsSelected($event)"
            @showTicketInfoModal="openTicketInfoModal($event)"
            @ticketTypeErrorUpdate="checkTicketTypeError($event)"
            @errorFound="errorFound"
            @clearTicketError="clearTicketError"
          />
        </table>
        <div class="pp-actions">
          <div class="pp-actions__container">
            <fancy-button
              :text="$t('buttons.add_to_basket')"
              @click.native="submitCheck"
              :icon="['far', 'ticket-alt']"
              :disabled="disabledCheck"
            />
          </div>
        </div>
      </section>
    </div>
    <modal-requires-id @submit="submit" />
    <modal-members-only/>
  </main>
</template>

<script>
import TicketItem from "@/components/TicketItem";
import ModalRequiresId from "@/components/modals/ModalRequiresId";
import FancyButton from "@/components/generic/FancyButton";
import CartAdditionsFailed from "@/components/shopping-cart/CartAdditionsFailed";
import Error from "@/components/generic/Error";
import ModalMembersOnly from "@/components/modals/ModalMembersOnly";
import { mapState, mapActions, mapGetters } from "vuex";

export default {
  name: "ChooseTickets",
  components: {
    TicketItem,
    ModalRequiresId,
    FancyButton,
    CartAdditionsFailed,
    Error,
    ModalMembersOnly
  },
  data() {
    return {
      ticketsSelected: {},
      ticketCount: 0,
      displayError: false,
      payload: {},
      errorMessage: "",
      ticketTypeName: "",
      ticketTypeLastModified: "",
      validationResult: false,
      maxAllowedError: false,
      discountError: false,
      failedAdditionClassCodes: [],
      successfulAdditionClassCodes: [],
      ticketItemsWithErrors: []
    };
  },
  watch: {
    ticketCount() {
      if (this.ticketCount > 40) {
        this.errorMessage = this.$t("errors.too_many_tickets");
        this.displayError = true;
      }
    },
    "$i18n.locale": function() {
      this.initMounted();
    }
  },
  async mounted() {
    await this.initMounted();
  },
  methods: {
    errorFound(item) {
      if(this.ticketItemsWithErrors.includes(item)) {
        // do nothing
      } else {
        this.ticketItemsWithErrors.push(item)
      }
    },
    clearTicketError(item) {
      let errorExistsIndex = this.ticketItemsWithErrors.indexOf(item);

      if(errorExistsIndex != -1) {
        this.ticketItemsWithErrors.splice(errorExistsIndex, 1)
      }
    },
    async initMounted() {
      if (this.performance.priceCategoryId != -1) {
        await this.getTicketsByPriceCategory(this.$i18n.locale);
      } else {
        await this.getAllTickets(this.$i18n.locale);
      }

      if(this.error == 'ticket_type_retrieval_failed') {
        this.triggerGoBack();
      }

      if(this.cart.membersOnlyTicketSelected) {
        this.$modal.show('modal-members-only');
      }

      if (this.someCartAdditionsFailed) {
        this.processCartAdditions();
      }
    },
    async submit() {
      this.updateLoading(true);
      this.clearError();
      this.clearCartAdditionTracking();
      this.updateStagedTickets(this.ticketsSelected);
      this.updateStagedIccCode(this.$route.params.icc);

      await this.retrieveUserDetails();

      this.updateLoading(true);

      let backend = process.env.VUE_APP_WK_CRM_URL;
      let currentUrl = window.location.protocol + "//" + window.location.host;
      if (this.user.userLoggedIn) {
        this.$router.push({
          name: "pre-cart",
          params: { icc: this.$route.params.icc, locale: this.$i18n.locale },
        });
      } else {
        window.location.href =
          backend +
          "/oauth/check?redirect=" +
          currentUrl +
          "/" +
          this.$i18n.locale +
          "/cart/processing/" +
          this.$route.params.icc +
          "&language=" +
          this.$i18n.locale;
      }
    },
    updateTicketsSelected(update) {
      // Remove any errors if the user modifies the ticket numbers.
      this.maxAllowedError = false;
      this.ticketItemsWithErrors = [];
      this.failedAdditionClassCodes = [];
      this.successfulAdditionClassCodes = [];
      this.clearError();
      this.clearCartAdditionTracking();
      if (this.discountError === false) {
        this.displayError = false;
        this.errorMessage = "";
        this.validationResult = true;
      }

      // ticketsSelected is an object, with ticket_type as a key
      this.ticketsSelected[update.ticket_type] = {
        quantity: update.quantity,
        promoPassword: update.discountCode,
        ticketTypeModified: update.ticketTypeModified,
        discountFieldShown: update.discountFieldShown,
      };

      this.ticketTypeLastModified = update.ticketTypeModified;

      // if we've reduced to zero tickets of this type, remove the key from the object
      if (this.ticketsSelected[update.ticket_type].quantity === 0) {
        delete this.ticketsSelected[update.ticket_type];
      }

      this.ticketCount = this.ticketCountCheck();
    },
    checkTicketTypeError(errors) {
      this.discountError = errors.discountError;
      this.maxAllowedError = errors.maxAllowedError;

      if ((this.maxAllowedError === false) && (this.discountError == false)) {
        this.displayError = false;
        this.errorMessage = "";
        this.validationResult = true;
      }
    },
    setValidationFailed() {
      this.displayError = true;
      return false;
    },
    ticketCountCheck() {
      let ticketCount = 0;

      Object.keys(this.ticketsSelected).forEach((key) => {
        ticketCount += parseInt(this.ticketsSelected[key].quantity);
      });

      return ticketCount;
    },
    concessionCheck() {
      // Per WK; this should be shown if the priceClassCode begins with H or HK, followed by two numbers
      // This supports any characters appearing after those initial 3/4
      let concessionRegex = new RegExp(/(^HK|^H)[0-9][0-9]/gi)

      let idCheckRequired = false;

      Object.keys(this.ticketsSelected).some(function(key) {
        if(concessionRegex.test(key)) idCheckRequired = true;
      });

      return idCheckRequired;
    },
    submitCheck() {
      // Validation check
      if (!this.validationCheck()) {
        return;
      }

      if (this.concessionCheck()) {
        this.$modal.show("modal-requires-id");
      } else {
        this.scrollToTopOfPurchasePath();
        this.submit();
      }
    },
    validationCheck() {
      // reset errors display before re-checking
      this.displayError = false;
      this.errorMessage = "";
      this.validationResult = true;

      if ((this.maxAllowedError === false) & (this.discountError == false)) {
        this.validationResult = true;
      } else {
        this.validationResult = false;
      }

      // If no ticket has been added (e.g. zero or NaN).
      if (!this.ticketCount) {
        this.validationResult = this.setValidationFailed();
      }

      // If the section is seated, check for the required seats selected.
      if (this.performance.mode != "GA" && this.ticketCount !== this.ticketsRequired) {
        this.errorMessage = this.$i18n.tc(
          "errors.select_all_tickets",
          this.ticketsRequired
        );
        this.validationResult = this.setValidationFailed();
      }

      // If we have an error displayed right under the specific ticket type.
      if (this.discountError || this.maxAllowedError) {
        this.errorMessage = this.$i18n.t("errors.fix_errors");
        this.validationResult = this.setValidationFailed();
      }

      // We check against empty but required (shown) discount code fields where
      // tickets have been added.
      const ticketTypeCodes = Object.keys(this.ticketsSelected);
      for (const key of ticketTypeCodes) {
        let ticketType = this.ticketsSelected[key];
        // We also check against validation result to make sure there are no other errors present.
        // We don't want to display this message if there are other validation errors
        if (
          ticketType.discountFieldShown &&
          !ticketType.promoPassword &&
          this.validationResult
        ) {
          this.errorMessage = this.$i18n.tc(
            "ticket.discount_code",
            this.ticketsRequired
          );
          this.validationResult = this.setValidationFailed();
        }
      }

      return this.validationResult;
    },
    openTicketInfoModal(update) {
      this.ticketTypeName = update.ticketType;
    },
    processCartAdditions() {
      this.cart.failedCartAdditions.forEach((item) => {
        // get the priceClassCode (discard the quantity & any promo code)
        let priceClassCode = item.priceClassMap.split(":");
        this.failedAdditionClassCodes.push(priceClassCode[0]);
      });

      this.cart.successfulCartAdditions.forEach((item) => {
        // get the priceClassCode (discard the quantity & any promo code)
        let priceClassCode = item.priceClassMap.split(":");
        this.successfulAdditionClassCodes.push(priceClassCode[0]);
      });
    },
    failedToAddTicketTypes() {
      return this.performance.ticketTypes.filter((item) => {
        return this.failedAdditionClassCodes.includes(item.priceClassCode);
      });
    },
    successfullyAddedTicketTypes() {
      return this.performance.ticketTypes.filter((item) => {
        return this.successfulAdditionClassCodes.includes(item.priceClassCode);
      });
    },
    ...mapActions([
      "updateLoading",
      "clearError",
      "getTicketsByPriceCategory",
      "getAllTickets",
      "updateStagedTickets",
      "updateStagedIccCode",
      "clearCartAdditionTracking",
      "retrieveUserDetails",
      "updateInventoryReservedList",
      "scrollToTopOfPurchasePath",
      "triggerGoBack"
    ]),
  },
  computed: {
    ticketsRequired() {
      let noOfTickets = 0;

      if (this.performance.mode !== "GA") {
        noOfTickets = this.cart.inventoryIdsReservedList.length;
      }
      return noOfTickets;
    },
    headingText() {
      return this.$i18n.tc("message.choose_tickets", this.ticketsRequired);
    },
    tooManyTickets() {
      if (this.ticketCount > this.ticketsRequired && this.ticketsRequired > 0) {
        return true
      } else {
        return false
      }
    },
    disabledCheck() {
      if(this.ticketItemsWithErrors.length > 0) {
        return true;
      }

      if (this.ticketCount <= 0 || this.ticketCount > 40) {
        return true;
      }

      if (this.tooManyTickets) {
        this.validationCheck();
      }

      if (this.validationResult == false) {
        return true;
      }

      return false;
    },
    ...mapState(["cart", "performance", "user", "error"]),
    ...mapGetters(["someCartAdditionsFailed"]),
  },
};
</script>
